// Made-up log of what Claude did and what you decided, for judging the design (see lib/sample.ts).
// The real log is kept by the Serb Mind vault run log.
import { sampleOnly } from '../../lib/sample';
import { jordanDate } from '@serb/shared';
import type { Tone } from '../../components/tone';
import type { ActivityType, LogEntry, LogStatus } from '@serb/shared';
import { addDays, shortDate } from '../../lib/dates';

export type { ActivityType, LogEntry, LogStatus };
export const ACTIVITY_TYPES: ActivityType[] = [
  'Rates',
  'Quotation',
  'Shipment',
  'Communication',
  'Financial',
  'Document',
  'Reminder',
  'System',
];
export const TYPE_TONE: Record<ActivityType, Tone> = {
  Rates: 'info',
  Quotation: 'info',
  Shipment: 'info',
  Communication: 'success',
  Financial: 'warning',
  Document: 'info',
  Reminder: 'error',
  System: 'neutral',
};

export const LOG_STATUSES: LogStatus[] = ['Completed', 'Pending Approval', 'Needs Attention'];
export const LOG_TONE: Record<LogStatus, Tone> = {
  Completed: 'success',
  'Pending Approval': 'warning',
  'Needs Attention': 'error',
};

export type RelatedKind = 'Customer' | 'Agent' | 'Carrier' | 'None';

/** A sample entry happened this many days before today, at `time` (Jordan time) */
type Raw = Omit<LogEntry, 'date'> & { daysAgo: number };

const E = (e: Raw): Raw => e;
const none = { related: '', relatedKind: 'None' as const };

const RAW: Raw[] = [
  E({
    id: 'l1',
    daysAgo: 0,
    time: '10:45',
    by: 'Claude',
    type: 'Rates',
    srb: 'SRB-711',
    related: 'Global Freight Ltd.',
    relatedKind: 'Agent',
    activity: 'Recorded agent rate',
    details: 'USD 6.40/kg (Air), TT 5 days',
    status: 'Completed',
    steps: [
      'Reviewed the incoming quotation from Global Freight',
      'Extracted the freight rate, transit time, and service details',
      'Recorded the agent rate in the rate history',
      'Linked the rate to shipment SRB-711',
      "Updated the shipment's pricing information",
      'Prepared a customer quotation for your approval',
    ],
    info: [
      ['Service', 'Air Freight'],
      ['Rate', 'USD 6.40/kg'],
      ['Transit Time', '5 days'],
      ['Routing', 'Guangzhou → Amman'],
      ['Source', 'Agent quotation email'],
    ],
    changes: [
      ['Agent Rate', 'Not recorded', 'USD 6.40/kg'],
      ['Rate Status', 'Awaiting', 'Received'],
    ],
    nextStep: 'Customer quotation is prepared and awaiting your approval.',
    source: 'Agent quotation email',
  }),
  E({
    id: 'l2',
    daysAgo: 0,
    time: '10:32',
    by: 'Claude',
    type: 'Quotation',
    srb: 'SRB-711',
    related: 'ABC Trading',
    relatedKind: 'Customer',
    activity: 'Prepared customer quotation',
    details: 'USD 1,550',
    status: 'Pending Approval',
    steps: [
      'Compared the agent rates for SRB-711',
      'Worked out the cost: USD 1,383',
      'Suggested a selling price of USD 1,550 (margin 10.8%)',
      'Put the quotation on an approval card for you',
    ],
    info: [
      ['Suggested price', 'USD 1,550'],
      ['Total cost', 'USD 1,383'],
      ['Margin', '10.8%'],
    ],
    changes: [['Quotation', 'Not prepared', 'Draft: USD 1,550']],
    nextStep: 'Customer quotation is prepared and awaiting your approval. Nothing was sent.',
  }),
  E({
    id: 'l3',
    daysAgo: 0,
    time: '10:15',
    by: 'Claude',
    type: 'Shipment',
    srb: 'SRB-640',
    related: 'Al-Rafedi',
    relatedKind: 'Customer',
    activity: 'Updated shipment schedule',
    details: 'ETA changed to 28 Jul 2025',
    status: 'Completed',
    changes: [['ETA', '27 Jul 2025', '28 Jul 2025']],
  }),
  E({
    id: 'l4',
    daysAgo: 0,
    time: '09:48',
    by: 'Claude',
    type: 'Communication',
    srb: 'SRB-539',
    related: 'Global Freight Ltd.',
    relatedKind: 'Agent',
    activity: 'Prepared email',
    details: 'Follow-up on booking confirmation',
    status: 'Pending Approval',
    nextStep: 'The email is prepared and awaiting your approval. Nothing was sent.',
  }),
  E({
    id: 'l5',
    daysAgo: 0,
    time: '09:30',
    by: 'Claude',
    type: 'Shipment',
    srb: 'SRB-596',
    related: 'Messina Line',
    relatedKind: 'Carrier',
    activity: 'Updated cargo details',
    details: 'Cargo ready date recorded',
    status: 'Completed',
  }),
  E({
    id: 'l6',
    daysAgo: 0,
    time: '09:12',
    by: 'Claude',
    type: 'Financial',
    srb: 'SRB-518',
    related: 'Turkon Line',
    relatedKind: 'Carrier',
    activity: 'Checked agent invoice',
    details: 'Missing invoice information',
    status: 'Needs Attention',
    nextStep: 'The agent invoice does not say what the charges are for. Ask the agent for a corrected invoice.',
  }),
  E({
    id: 'l7',
    daysAgo: 0,
    time: '08:45',
    by: 'Claude',
    type: 'System',
    srb: '',
    ...none,
    activity: 'Operational check',
    details: 'Reviewed 12 active shipments',
    status: 'Completed',
  }),
  E({
    id: 'l8',
    daysAgo: 1,
    time: '16:20',
    by: 'You',
    type: 'Financial',
    srb: 'SRB-539',
    related: 'TechnoPack',
    relatedKind: 'Customer',
    activity: 'Approved customer invoice',
    details: 'JOD 3,200',
    status: 'Completed',
  }),
  E({
    id: 'l9',
    daysAgo: 1,
    time: '15:10',
    by: 'Claude',
    type: 'Document',
    srb: 'SRB-640',
    related: 'Al-Rafedi',
    relatedKind: 'Customer',
    activity: 'Registered document',
    details: 'Original BL uploaded',
    status: 'Completed',
  }),
  E({
    id: 'l10',
    daysAgo: 1,
    time: '14:35',
    by: 'Claude',
    type: 'Communication',
    srb: 'SRB-625',
    related: 'Mersin Forwarding',
    relatedKind: 'Agent',
    activity: 'Prepared email',
    details: 'Rate request for next shipment',
    status: 'Pending Approval',
  }),
  E({
    id: 'l11',
    daysAgo: 1,
    time: '13:22',
    by: 'Claude',
    type: 'Rates',
    srb: 'SRB-625',
    related: 'Mersin Forwarding',
    relatedKind: 'Agent',
    activity: 'Received agent rate',
    details: 'USD 95/CBM (LCL), TT 3 days',
    status: 'Completed',
  }),
  E({
    id: 'l12',
    daysAgo: 1,
    time: '12:10',
    by: 'Claude',
    type: 'Shipment',
    srb: 'SRB-701',
    related: 'Turkon Line',
    relatedKind: 'Carrier',
    activity: 'Updated status',
    details: 'Departed Hamburg (ETD 16 Oct)',
    status: 'Completed',
  }),
  E({
    id: 'l13',
    daysAgo: 1,
    time: '11:45',
    by: 'Claude',
    type: 'Reminder',
    srb: 'SRB-518',
    related: 'Valencia Logistics',
    relatedKind: 'Agent',
    activity: 'Created reminder',
    details: 'Follow up on DG approval',
    status: 'Completed',
  }),
  E({
    id: 'l14',
    daysAgo: 1,
    time: '10:18',
    by: 'Claude',
    type: 'Financial',
    srb: 'SRB-711',
    related: 'Global Freight Ltd.',
    relatedKind: 'Agent',
    activity: 'Updated cost estimate',
    details: 'Total cost updated to USD 1,383',
    status: 'Completed',
  }),
  E({
    id: 'l15',
    daysAgo: 2,
    time: '17:05',
    by: 'Claude',
    type: 'Communication',
    srb: 'SRB-539',
    related: 'TechnoPack',
    relatedKind: 'Customer',
    activity: 'Summarized conversation',
    details: 'Agent confirmed new ETD 9 Jun',
    status: 'Completed',
  }),
  E({
    id: 'l16',
    daysAgo: 2,
    time: '15:40',
    by: 'Claude',
    type: 'Shipment',
    srb: 'SRB-711',
    related: 'ABC Trading',
    relatedKind: 'Customer',
    activity: 'Created shipment',
    details: 'Guangzhou → Amman (Air)',
    status: 'Completed',
  }),
  E({
    id: 'l17',
    daysAgo: 2,
    time: '14:12',
    by: 'Claude',
    type: 'Rates',
    srb: 'SRB-640',
    related: 'Global Freight Ltd.',
    relatedKind: 'Agent',
    activity: 'Compared rates',
    details: '3 options analyzed',
    status: 'Completed',
  }),
  E({
    id: 'l18',
    daysAgo: 2,
    time: '11:33',
    by: 'Claude',
    type: 'Document',
    srb: 'SRB-596',
    related: 'Horizon Co.',
    relatedKind: 'Customer',
    activity: 'Updated document status',
    details: 'Packing list received',
    status: 'Completed',
  }),
  E({
    id: 'l19',
    daysAgo: 2,
    time: '09:55',
    by: 'Claude',
    type: 'System',
    srb: '',
    ...none,
    activity: 'Data synchronization',
    details: 'Fleet data updated',
    status: 'Completed',
  }),
  E({
    id: 'l20',
    daysAgo: 3,
    time: '16:20',
    by: 'Claude',
    type: 'Quotation',
    srb: 'SRB-648',
    related: 'TechnoPack',
    relatedKind: 'Customer',
    activity: 'Prepared revised quotation',
    details: 'EUR 3,800',
    status: 'Pending Approval',
  }),
  E({
    id: 'l21',
    daysAgo: 3,
    time: '14:02',
    by: 'Claude',
    type: 'Communication',
    srb: 'SRB-703',
    related: 'Aqua Shipping',
    relatedKind: 'Agent',
    activity: 'Prepared email',
    details: 'Pre-arrival notice',
    status: 'Completed',
  }),
  E({
    id: 'l22',
    daysAgo: 4,
    time: '10:40',
    by: 'You',
    type: 'Quotation',
    srb: 'SRB-640',
    related: 'Al-Rafedi',
    relatedKind: 'Customer',
    activity: 'Approved customer quotation',
    details: 'EUR 2,000',
    status: 'Completed',
  }),
  E({
    id: 'l23',
    daysAgo: 5,
    time: '09:15',
    by: 'Claude',
    type: 'Rates',
    srb: 'SRB-518',
    related: 'Valencia Logistics',
    relatedKind: 'Agent',
    activity: 'Recorded agent rate',
    details: 'USD 2,980 (FCL), TT 18 days',
    status: 'Completed',
  }),
  E({
    id: 'l24',
    daysAgo: 6,
    time: '16:45',
    by: 'Claude',
    type: 'Financial',
    srb: 'SRB-625',
    related: 'Sky Logistics',
    relatedKind: 'Agent',
    activity: 'Flagged low margin',
    details: 'Margin 3.2% is under 5%',
    status: 'Needs Attention',
  }),
];

export const LOG: LogEntry[] = sampleOnly(
  RAW.map(({ daysAgo, ...e }) => ({ ...e, date: addDays(jordanDate(), -daysAgo) })),
);

/** The date of an entry, "2026-10-18" */
export const dateOf = (e: LogEntry) => e.date;

/** "18 Oct 2026, 10:45" (just the date when the vault only records the day) */
export const whenOf = (e: LogEntry) => `${shortDate(e.date)}${e.time ? `, ${e.time}` : ''}`;
